/**
 * Radar product identity.
 *
 * The mark is a Bauhaus 2x2 grid on a 64x64 canvas: square, quarter-disc,
 * circle, square. The three neutral shapes are `currentColor` so the mark
 * inherits the surrounding text token and stays correct on the dark default
 * theme, the light theme, `ForceTheme`-locked pages, and `@media print` --
 * all without a theme hook or a hydration flash. The quarter-disc keeps a
 * literal `#F90B8A`: it is the brand constant in every variant of the source
 * artwork, and a logo must not re-color if `--cta` is ever retuned.
 *
 * This is the same fix as `PoweredByPaddle` / `EngineLogos` -- external SVGs
 * loaded through `next/image` cannot inherit CSS color and go invisible on
 * whichever theme fights their hardcoded fill.
 *
 * Static artwork for the cases CSS cannot reach (email rasterization, the
 * press kit) lives in `public/radar-branding/`. Read this
 * before using it:
 *
 * - `radar-icon-on-dark.svg` / `radar-icon-on-light.svg` are pure geometry with
 *   no text. They are deterministic and safe to rasterize anywhere.
 * - `radar-lockup-on-dark.svg` / `radar-lockup-on-light.svg` still carry live
 *   `<text>` in Space Grotesk and Geist Mono. Any renderer without those fonts
 *   substitutes and the wordmark shifts. Verified: rasterizing them with sharp
 *   on a machine lacking Space Grotesk renders the wordmark in a fallback sans.
 *
 * So the lockups MUST be outlined (text converted to `<path>`) before they are
 * wired into headless rasterization or published in the press kit. Headless is
 * the dangerous case: a substituted wordmark baked into an email PNG ships
 * without anyone seeing it. Nothing consumes the lockups today, which is the
 * only reason this is still open.
 */

/** Brand pink. Constant across both background variants of the mark. */
const RADAR_PINK = '#F90B8A'

interface RadarMarkProps {
  className?: string
  /**
   * Accessible name. Provide it only when the mark stands alone; when it sits
   * beside a visible "Radar" label it renders decorative so screen readers do
   * not announce the product twice.
   */
  label?: string
}

export function RadarMark({ className = '', label }: RadarMarkProps) {
  return (
    <svg
      viewBox='0 0 64 64'
      fill='none'
      className={className}
      {...(label
        ? { role: 'img', 'aria-label': label }
        : { 'aria-hidden': true })}
    >
      <rect x='4' y='4' width='26' height='26' fill='currentColor' />
      <path
        d='M34 4 L34 30 A26 26 0 0 0 60 4 Z'
        fill={RADAR_PINK}
        className='mark-verb'
      />
      <circle cx='17' cy='47' r='8' fill='currentColor' />
      <rect x='34' y='34' width='26' height='26' fill='currentColor' />
    </svg>
  )
}

/**
 * `sm` is app/report chrome, where the lockup sits in a 56px nav beside other
 * controls. `md` is a section eyebrow above a `.thesis-headline`: enough
 * presence to register as the section's mark, still clearly subordinate to the
 * headline. `lg` is a standalone nameplate -- a centered marketing hero, where
 * chrome sizing reads undersized against a display headline.
 *
 * `lg` scales at the `md` breakpoint on purpose. The hero headline it sits
 * above is `clamp(2.25rem, 7vw, 5rem)`, so it shrinks to 36px on mobile; a
 * fixed 34px wordmark would nearly match it and fight the headline for
 * attention on exactly the smallest screens.
 */
const LOCKUP_SIZES = {
  sm: {
    mark: 'h-7 w-7',
    gap: 'gap-2.5',
    word: 'text-[17px]',
    byline: 'text-[9px]',
  },
  md: {
    mark: 'h-10 w-10',
    gap: 'gap-3',
    word: 'text-[24px]',
    byline: 'text-[10px]',
  },
  lg: {
    mark: 'h-10 w-10 md:h-14 md:w-14',
    gap: 'gap-3 md:gap-4',
    word: 'text-[24px] md:text-[34px]',
    byline: 'text-[10px] md:text-[12px]',
  },
} as const

interface RadarLockupProps {
  className?: string
  size?: keyof typeof LOCKUP_SIZES
  /**
   * Renders the "BY PIXELMOJO" byline. On by default -- the lockup is what
   * carries the Pixelmojo attribution wherever the Radar mark replaces the
   * corporate logo. Turn off only where the parent already says it.
   */
  byline?: boolean
}

/**
 * Mark plus wordmark. The wordmark is real text rather than SVG `<text>` so it
 * stays selectable and accessible, and so it renders in Space Grotesk via the
 * loaded `--font-display` instead of depending on the font being available to
 * an SVG renderer.
 */
export function RadarLockup({
  className = '',
  size = 'sm',
  byline = true,
}: RadarLockupProps) {
  const s = LOCKUP_SIZES[size]
  return (
    <span className={`inline-flex items-center ${s.gap} ${className}`}>
      <RadarMark className={`${s.mark} shrink-0`} />
      <span className='flex flex-col justify-center'>
        <span
          className={`font-display ${s.word} font-semibold leading-none tracking-[-0.02em] text-foreground`}
        >
          Radar
        </span>
        {byline && (
          <span
            className={`mt-1 font-mono ${s.byline} font-medium uppercase leading-none tracking-[0.18em] text-muted-foreground`}
          >
            by Pixelmojo
          </span>
        )}
      </span>
    </span>
  )
}
